import { apiRequest } from '@lib/api';

import {
    AddressAvailability,
    ClientDetailResponse,
    ClientListResponse,
    CreatedClient,
    ProvisionedOwner,
    UsernameAvailability,
    type ClientDetail,
    type ClientSummary,
    type CreateClientInput,
} from '../schemas/client';
import { targetParams, type ClientTarget } from '../schemas/target';

/**
 * The clients API, one function per route.
 *
 * Every one takes a {@link ClientTarget} and puts it on the query string. There
 * is no default and no ambient value: the server refuses a request that does
 * not say where it is aimed, and on a create that refusal is the difference
 * between a client in dev and a client in production.
 */

function query(target: ClientTarget, extra: Record<string, string> = {}): string {
    return new URLSearchParams({ ...targetParams(target), ...extra }).toString();
}

export async function listClients(
    target: ClientTarget,
    signal?: AbortSignal,
): Promise<ClientSummary[]> {
    const { clients } = await apiRequest(`/clients?${query(target)}`, ClientListResponse, {
        signal,
    });
    return clients;
}

export async function getClient(
    target: ClientTarget,
    tenantId: string,
    signal?: AbortSignal,
): Promise<ClientDetail | null> {
    const { client } = await apiRequest(
        `/clients/${encodeURIComponent(tenantId)}?${query(target)}`,
        ClientDetailResponse,
        { signal },
    );
    return client;
}

/**
 * Is a public address free?
 *
 * A read, so it can be stale by the time the create runs — another operator
 * fits in between. The authority stays the create itself, which is why
 * `ADDRESS_TAKEN` is still handled there and must not be dropped because this
 * exists. What this buys is finding the common case on the step that owns the
 * field, instead of after three of them are filled in.
 */
export function checkAddress(
    target: ClientTarget,
    slug: string,
    signal?: AbortSignal,
): Promise<AddressAvailability> {
    return apiRequest(
        `/clients/address-availability?${query(target, { slug })}`,
        AddressAvailability,
        {
            signal,
        },
    );
}

/**
 * Give an existing client its first person.
 *
 * Not a retry of the creation: that would fail on the address this very client
 * holds. It is a different operation, keyed on the tenant id.
 */
export function provisionOwner(
    target: ClientTarget,
    tenantId: string,
    owner: { username: string; email: string },
): Promise<ProvisionedOwner> {
    return apiRequest(
        `/clients/${encodeURIComponent(tenantId)}/owner?${query(target)}`,
        ProvisionedOwner,
        { method: 'POST', body: owner },
    );
}

export function createClient(
    target: ClientTarget,
    input: CreateClientInput,
): Promise<CreatedClient> {
    return apiRequest(`/clients?${query(target)}`, CreatedClient, {
        method: 'POST',
        body: input,
    });
}

/**
 * Is a login handle free?
 *
 * A POST for a read, and the handle travels in the body — a query parameter
 * would put it into every access log on the way. Same caveat as the address:
 * a snapshot, not a guarantee, so `OWNER_CONFLICT` stays handled on create.
 *
 * Unlike the address, this is answered **globally**: a handle is unique across
 * every client, so "taken" can mean taken by somebody at another one.
 */
export function checkUsername(
    target: ClientTarget,
    username: string,
    signal?: AbortSignal,
): Promise<UsernameAvailability> {
    return apiRequest(`/clients/username-availability?${query(target)}`, UsernameAvailability, {
        method: 'POST',
        body: { username },
        signal,
    });
}
